import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { isPositiveNumber, isDefinedString, reduce } from "et3-type-guards";
import { colors } from "../../core/et3-theme/colors";
import {
  ExtraDataEvents,
  HeaderWithTypeField,
  LinkComponent,
  MessageType,
  ReasonsArrayDisplay,
  HeaderWithSubtitle,
  OldExtraDataEvents,
  ClientReadableText
} from "../../core/et3-table/UserCustomHelpers";
import ET3Div from "../../core/et3-div";
import getUserMessagesStore from "../../stores/user-store/messages-store";
import getCorePersonalStore from "../../stores/core/core-personal-store";
import i18n from "../i18n";

export const USER_ADMIN_COMMENTS_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Admin Name",
    field: "adminName"
  },
  {
    headerName: "Comment",
    field: "adminComment"
  },
  {
    headerName: "Date",
    field: "commentDate"
  }
];

export const USER_ADVANCED_HEADERS: GridColDef[] = [
  {
    headerName: "NO #",
    field: "uniqueId"
  },
  {
    headerName: "Type",
    field: "attribut"
  },
  {
    headerName: "Value",
    field: "value"
  }
];

export const USER_ADVANCED_EMAILS_HEADERS: GridColDef[] = [
  {
    headerName: "NO #",
    field: "uniqueId",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Email",
    field: "userEmail",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Platfrom",
    field: "verificationPlatform",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Verification Active",
    field: "verificationActive",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "User Activity Emails Subscription",
    field: "userActivityEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Promotional Emails Subscription",
    field: "promotionalEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "New Message Emails Subscription",
    field: "newMessageEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Visit Profile Emails Subscription",
    field: "visitProfileEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Like Emails Subscription",
    field: "likeEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "User Activity Emails Settings",
    field: "userActivityEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Promotional Emails Settings",
    field: "promotionalEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "New Message Emails Settings",
    field: "newMessageEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Visit Profile Emails Settings",
    field: "visitProfileEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: "Like Emails Settings",
    field: "likeEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  }
];

export const USER_ADVANCED_QUIZ_HEADERS: GridColDef[] = [
  {
    headerName: "Question Id",
    field: "questionId"
  },
  {
    headerName: "Allowed Answers",
    field: "allowedAnswers"
  },
  {
    headerName: "Question",
    field: "questionTextEnglish",
    flex: 2.5
  },
  {
    headerName: "Answer",
    field: "answerTextEnglish",
    flex: 2.5
  }
];

export const USER_EVENTS_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId",
    flex: 0.3
  },
  {
    headerName: "Admin Name",
    field: "adminName"
  },
  {
    headerName: "Type",
    field: "type",
    renderCell: (params: GridRenderCellParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Old Extra Data",
    field: "oldExtraData",
    renderCell: (params: GridRenderCellParams) => {
      return (
        <OldExtraDataEvents
          eventType={params.row.type}
          extraData={params.row.extraData}
        />
      );
    }
  },
  {
    headerName: "Extra Data",
    field: "extraData",
    renderCell: (params: GridRenderCellParams) => {
      return (
        <ExtraDataEvents
          eventType={params.row.type}
          imageSource={params.row.image_source}
          description={params.row.description}
          reportID={params.row.reportID}
          temporaryBanNumberOfDays={params.row.temporaryBanNumberOfDays}
          temporaryBanUntil={params.row.temporaryBanUntil}
          investigationCaseID={params.row.investigationCaseID}
          extraData={params.row.extraData}
        />
      );
    }
  },
  {
    headerName: "Page Name",
    field: "pageName",
    renderCell: (params: GridRenderCellParams) => {
      const { pageName, userID, reportID } = params.row;
      let hrefVal = `/users/advanced?id=${userID}`;
      const isAnyKhattabaAdmin =
        getCorePersonalStore().isAnyKhattabaAdmin.get();

      if (isPositiveNumber(userID)) {
        if (pageName === "USER_ADVANCED") {
          hrefVal = `/users/advanced?id=${userID}`;
        }
        if (pageName === "USER_CONVERSATIONS") {
          hrefVal = `/users/messages?id=${userID}`;
        }
        if (pageName === "REPORTS") {
          hrefVal = `/reports?id=${reportID}`;
        }
      }
      return isAnyKhattabaAdmin ? (
        <span>{pageName}</span>
      ) : isDefinedString(pageName) ? (
        <LinkComponent href={`${hrefVal}`} text={`${pageName}`} style={{}} />
      ) : (
        <LinkComponent href={`${hrefVal}`} text={`USER ADVANCED`} style={{}} />
      );
    }
  },
  {
    headerName: "TBW Reasons",
    field: "tbw_reasons",
    renderCell: (params: GridRenderCellParams) => {
      return <ReasonsArrayDisplay reasons={params.row.tbw_reasons} />;
    }
  },
  {
    headerName: "Date",
    field: "date"
  }
];

export const USER_EVENTS_VERIFY_EMAIL_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Date",
    field: "date"
  },
  {
    headerName: "Email Used",
    field: "email"
  },
  {
    headerName: "Verified",
    field: "verified"
  }
];

export const USER_DEACTIVATIONS_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Type",
    field: "type"
  },
  {
    headerName: "Date",
    field: "date"
  }
];

export const USER_MESSAGES_HEADERS: GridColDef[] = [
  {
    headerName: "Message ID",
    field: "messageId"
  },
  {
    headerName: "Sender",
    field: "senderId",
    renderCell: (params: GridRenderCellParams) => {
      const hrefVal = `/users/advanced?id=${params.row.senderId}`;
      return (
        <LinkComponent
          href={`${hrefVal}`}
          text={params.row.senderId}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Recipient",
    field: "recipientId",
    renderCell: (params: GridRenderCellParams) => {
      const { recipientId } = params.row;
      const href = `/users/advanced?id=${recipientId}`;
      return (
        <ET3Div
          isScrollable={false}
          isExtendable={false}
          fDirection="row"
          justifyContent="center"
          alignItems="center"
          withShadow={false}
          width={180}
          height={50}
          color={colors.all.transparent}
          style={{}}
          onClick={() => {
            getUserMessagesStore().setRecipientColored(recipientId, true);
          }}
        >
          <LinkComponent href={`${href}`} text={recipientId} style={{}} />
        </ET3Div>
      );
    }
  },
  {
    headerName: "Message",
    field: "message",
    flex: 3,
    renderCell: (params: GridRenderCellParams) => {
      const message = reduce(() => params.row.messageObject.message, "");
      const messageType = reduce(
        () => params.row.messageObject.messageType,
        ""
      );
      return <MessageType message={message} messageType={messageType} />;
    }
  },
  {
    headerName: "Sent On",
    field: "sentOn"
  },
  {
    headerName: "Message Type",
    field: "messageType"
  }
];

export const SUBSCRIPTION_PAYMENT_HISTORY_HEADERS: GridColDef[] = [
  {
    headerName: "User ID",
    field: "userID"
  },
  {
    headerName: "Transaction ID",
    field: "transactionID",
    flex: 2.5
  },
  {
    headerName: "Invoice Number",
    field: "invoiceID",
    flex: 1.5
  },
  {
    headerName: "Platform",
    field: "platform"
  },
  {
    headerName: "Country",
    field: "country"
  },
  {
    headerName: "Payment Method",
    field: "paymentMethod"
  },
  {
    headerName: "Payment Source",
    field: "paymentSource"
  },
  {
    headerName: "Subscription Status",
    field: "subscriptionStatus",
    flex: 2,
    renderCell: (params: GridRenderCellParams) => {
      return <HeaderWithTypeField value={params.row.subscriptionStatus} />;
    }
  },
  {
    headerName: "Event Date",
    field: "createdOn"
  },
  {
    headerName: "Expiry Date",
    field: "expiryDate"
  },
  {
    headerName: "Amount",
    field: "amount",
    flex: 1.5,
    renderHeader: () => {
      return (
        <HeaderWithSubtitle
          title={i18n.get("AMOUNT")}
          subtitle={i18n.get("FREE_PAID_IN_HOURS_DAYS")}
        />
      );
    }
  },
  {
    headerName: "Description",
    field: "description",
    flex: 2,
    renderCell: (params: GridRenderCellParams) => {
      return <ClientReadableText text={params.row.description} />;
    }
  }
];

export const SUBSCRIPTION_REWIND_BOOST_PAYMENT_HISTORY_HEADERS: GridColDef[] = [
  {
    headerName: "User ID",
    field: "userID"
  },
  {
    headerName: "Transaction ID",
    field: "transactionID",
    flex: 2.5
  },
  {
    headerName: "Invoice Number",
    field: "invoiceID",
    flex: 1.5
  },
  {
    headerName: "Platform",
    field: "platform"
  },
  {
    headerName: "Country",
    field: "country"
  },
  {
    headerName: "Payment Method",
    field: "paymentMethod"
  },
  {
    headerName: "Payment Source",
    field: "paymentSource"
  },
  {
    headerName: "Subscription Status",
    field: "subscriptionStatus",
    flex: 2,
    renderCell: (params: GridRenderCellParams) => {
      return <HeaderWithTypeField value={params.row.subscriptionStatus} />;
    }
  },
  {
    headerName: "Event Date",
    field: "createdOn"
  },
  {
    headerName: "Expiry Date",
    field: "expiryDate"
  },
  {
    headerName: "Amount",
    field: "amount",
    flex: 1.5,
    renderHeader: () => {
      return (
        <HeaderWithSubtitle
          title={i18n.get("AMOUNT")}
          subtitle={i18n.get("COUNT_HINT_SUBTEXT")}
        />
      );
    }
  },
  {
    headerName: "Description",
    field: "description",
    flex: 2,
    renderCell: (params: GridRenderCellParams) => {
      return <ClientReadableText text={params.row.description} />;
    }
  }
];
